import { Link } from 'react-router-dom';
import React from 'react';

const MENTION_REGEX = /@\[id:([a-f0-9A-F]{24})\]\(([^)]*)\)/g;

/**
 * Parse text and return array of React nodes: strings and <Link> for mentions.
 * Use in task description and comment text to render @[id:xxx](Name) as links.
 * Mention links go to the user's profile page (/users/:id).
 */
export function renderTextWithMentions(text, _linkToTaskId = null) {
  if (!text || typeof text !== 'string') return null;
  const parts = [];
  let lastIndex = 0;
  let m;
  MENTION_REGEX.lastIndex = 0;
  while ((m = MENTION_REGEX.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index));
    }
    const userId = m[1];
    const displayName = (m[2] || '').trim() || 'User';
    const to = `/users/${userId}`;
    parts.push(
      <Link key={`${userId}-${m.index}`} to={to} className="mention-link">
        {displayName}
      </Link>
    );
    lastIndex = MENTION_REGEX.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length === 0 ? text : <>{parts}</>;
}

/**
 * Convert backend format @[id:xxx](Name) to display format @Name (for use in inputs).
 */
export function toMentionDisplayText(text) {
  if (!text || typeof text !== 'string') return text;
  return text.replace(MENTION_REGEX, (_, _id, name) => `@${(name || '').trim() || 'User'}`);
}

/**
 * Parse mentions from text (backend format) in order of appearance. Returns [{ userId, displayName }].
 */
export function parseMentionsOrdered(text) {
  if (!text || typeof text !== 'string') return [];
  const list = [];
  let m;
  MENTION_REGEX.lastIndex = 0;
  while ((m = MENTION_REGEX.exec(text)) !== null) {
    list.push({ userId: m[1], displayName: (m[2] || '').trim() || 'User' });
  }
  return list;
}

/**
 * Convert display-format text (@Name) to backend format @[id:xxx](Name) using ordered mention list.
 * Uses mentionList displayNames so full names with spaces (e.g. "Maryam Rajput") are replaced as a whole,
 * avoiding duplicate last name when the previous regex stopped at the first space.
 * mentionList: [{ userId, displayName }] in same order as @ mentions in text.
 */
export function toBackendMentionFormat(text, mentionList) {
  if (!text || !mentionList?.length) return text;
  let result = text;
  let idx = 0;
  const mentionStarts = [];
  for (let j = 0; j < result.length; j++) {
    if (result[j] === "@" && (j === 0 || /\s/.test(result[j - 1]))) {
      mentionStarts.push(j);
    }
  }
  // Replace from end to start so indices don't shift
  for (let k = mentionStarts.length - 1; k >= 0; k--) {
    const entry = mentionList[k];
    if (!entry) continue;
    const start = mentionStarts[k];
    const displayName = (entry.displayName || "User").trim();
    const afterAt = result.slice(start + 1);
    let nameLen = 0;
    for (let i = 0; i < displayName.length && afterAt[i] === displayName[i]; i++) {
      nameLen++;
    }
    if (nameLen === displayName.length) {
      if (afterAt[nameLen] === " ") nameLen++;
      const replaceWith = `@[id:${entry.userId}](${entry.displayName})`;
      result = result.slice(0, start) + replaceWith + result.slice(start + 1 + nameLen);
    }
  }
  return result;
}

/**
 * Replace the range [replaceStart, replaceEnd) with a mention. Inserts only @Display Name (no ID visible).
 * Returns { newValue, newPos }. Caller should add { userId, displayName } to mention list for submit.
 */
export function insertMentionAtCursor(value, replaceStart, replaceEnd, _userId, displayName) {
  const mention = `@${displayName} `;
  const before = value.slice(0, replaceStart);
  const after = value.slice(replaceEnd);
  const newValue = before + mention + after;
  const newPos = replaceStart + mention.length;
  return { newValue, newPos };
}
